import React, { useState, useEffect } from 'react';
import styled, { ThemeProvider, DefaultTheme } from 'styled-components';
import ReactSelect, { MultiValue, StylesConfig } from 'react-select';

// Define the theme type
type ThemeType = 'light' | 'dark';

// Define the theme colors interface
interface ThemeColors {
  text: string;
  background: string;
  border: string;
  primary: string;
  primaryHover: string;
  secondary: string;
  error: string;
  disabled: string;
  textPrimary: string;
  textSecondary: string;
  surface: string;
  textMuted: string;
  borderLight: string;
  success: string;
  warning: string;
  info: string;
}



// Theme configuration
export const lightTheme: DefaultTheme = {
  mode: 'light',
  colors: {
    text: '#2d3748',
    background: '#ffffff',
    border: '#e2e8f0',
    primary: '#4299e1',
    primaryHover: '#3182ce',
    secondary: '#718096',
    error: '#e53e3e',
    disabled: '#a0aec0',
    textPrimary: '#2d3748',
    textSecondary: '#718096',
    surface: '#f7fafc',
    textMuted: '#a0aec0',
    borderLight: '#e2e8f0',
    success: '#10b981',
    warning: '#f59e0b',
    info: '#3b82f6',
  },
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
  },
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
  },
  borderRadius: {
    sm: '0.25rem',
    md: '0.375rem',
    lg: '0.5rem',
    full: '9999px',
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  transitions: {
    default: '0.2s ease-in-out',
    fast: '0.1s ease-in-out',
    slow: '0.3s ease-in-out',
  },
};

export const darkTheme: DefaultTheme = {
  ...lightTheme,
  mode: 'dark',
  colors: {
    ...lightTheme.colors,
    text: '#e2e8f0',
    background: '#2d3748',
    border: '#4a5568',
    secondary: '#a0aec0',
    disabled: '#718096',
    textPrimary: '#e2e8f0',
    textSecondary: '#a0aec0',
    surface: '#1a202c',
    textMuted: '#718096',
    borderLight: '#4a5568',
  },
};

// Helper function to get theme
const getTheme = (themeMode: ThemeType): DefaultTheme => {
  return themeMode === 'dark' ? darkTheme : lightTheme;
};

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.92);
  color: ${({ theme }) => theme.colors.text};
  border-radius: 2rem;
  width: 98%;
  max-width: 700px;
  min-width: 420px;
  padding: 2.8rem 2.4rem 2.2rem 2.4rem;
  box-shadow: 0 8px 32px 0 rgba(0,0,0,0.18), 0 2px 4px 0 rgba(0,0,0,0.05);
  border: 1.5px solid rgba(0,0,0,0.10);
  backdrop-filter: blur(10px);
  position: relative;
  transition: box-shadow 0.2s, border 0.2s;
  overflow: visible;
`;

const ModalHeaderStyled = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.1rem 0 0.8rem 0;
  border-bottom: 1.5px solid rgba(0,0,0,0.10);
  background: transparent;
`;

const ModalTitleStyled = styled.h2`
  margin: 0;
  font-size: 1.08rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
  letter-spacing: 0.01em;
  background: none;
  text-align: center;
`;


const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  letter-spacing: 0.01em;
  flex: 1 1 auto;
  padding-left: 0.3rem;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.1rem;
  right: 1.4rem;
  background: none;
  border: none;
  font-size: 1.6rem;
  cursor: pointer;
  color: #7b8794;
  width: 2.2rem;
  height: 2.2rem;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.18s, color 0.18s;
  z-index: 3;

  &:hover, &:focus {
    background: rgba(255,255,255,0.85);
    color: #fff;
    outline: none;
  }

  /* Make the × always centered and smooth */
  span, svg {
    display: block;
    font-size: 1.6rem;
    line-height: 1;
    font-weight: 700;
    pointer-events: none;
    transition: color 0.18s;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
`;

const StyledSelect = styled.select<{ disabled?: boolean }>`
  width: 100%;
  padding: 0.75rem 1.15rem;
  border-radius: 1.2rem;
  border: 1.5px solid rgba(0,0,0,0.10);
  background: rgba(255,255,255,0.7);
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  box-shadow: 0 1px 2px rgba(0,0,0,.03);
  transition: border 0.15s;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 2px #4299e1;
  }

  option {
    background: #fff;
    color: ${({ theme }) => theme.colors.text};
    transition: background 0.12s;
  }

  option:hover {
    background: rgba(66,153,225,0.13); /* Slightly stronger for cursor hover */
    color: ${({ theme }) => theme.colors.text};
  }

  option:checked,
  option[selected] {
    background: rgba(66,153,225,0.11); /* theme.colors.primary with 0.11 opacity */
    color: ${({ theme }) => theme.colors.text};
  }

`;


const Input = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #4299e1;
    box-shadow: 0 0 0 1px #4299e1;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.7rem 1.4rem;
  border-radius: 1.3rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0,0,0,.04);
  border: none;
  margin-left: 0.5rem;
  
  ${({ variant = 'secondary', theme }) => 
    variant === 'primary'
      ? `
        background: ${theme.colors.primary};
        color: #fff;
        border: none;

        &:hover {
          background: ${theme.colors.primaryHover};
        }
      `
      : `
        background: rgba(0,0,0,0.04);
        color: ${theme.colors.primary};
        border: 1.5px solid ${theme.colors.primary};

        &:hover {
          background: rgba(0,0,0,0.07);
        }
      `}
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: none;
`;

const SaveButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  font-size: 1.08rem;
  font-weight: 600;
  padding: 0.57rem 1.7rem;
  border-radius: 0.8rem;
  cursor: pointer;
  box-shadow: 0 1px 4px 0 rgba(0,0,0,0.07);
  transition: background 0.13s, box-shadow 0.13s;
  outline: none;
  &:hover {
    background: ${({ theme }) => theme.colors.primaryHover || theme.colors.primary};
    box-shadow: 0 2px 8px 0 rgba(66,153,225,0.09);
  }
`;


const VerticalDivider = styled.div`
  width: 1px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.10), rgba(0,0,0,0.06) 90%);
  margin: 0 1.6rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  min-height: 220px;
`;

const DividerText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-90deg);
  background: none;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 0.91rem;
  font-weight: 500;
  padding: 0.15rem 0.7rem;
  pointer-events: none;
  opacity: 0.7;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.secondary};
  
  input {
    margin-right: 0.5rem;
  }
`;

const Placeholder = styled.span`
  color: ${({ theme }) => theme.colors.disabled};
  font-size: 0.875rem;
`;

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme.colors.error};
  font-size: 0.75rem;
  margin-top: 0.25rem;
`;

const Section = styled.section`
  margin-bottom: 2.2rem;
  padding-bottom: 1.2rem;
  border-bottom: 1px solid rgba(0,0,0,0.07);

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.08rem;
  font-weight: 600;
  margin: 0 0 1.1rem 0;
  color: ${({ theme }) => theme.colors.primary};
  letter-spacing: 0.01em;
  padding-left: 0.1rem;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2.2rem;
  width: 100%;
  min-height: 340px;
  margin-top: 0.4rem;
`;

const SidebarTabs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 130px;
  padding-top: 0.3rem;
  padding-bottom: 0.3rem;
  align-items: flex-start;
`;

const TabButtonVertical = styled.button<{ active: boolean }>`
  font-size: 1.04rem;
  font-weight: 600;
  color: ${({ active, theme }) => (active ? theme.colors.primary : theme.colors.secondary)};
  background: ${({ active }) => (active ? 'rgba(66,153,225,0.09)' : 'transparent')};
  border: none;
  border-radius: 1.1rem;
  padding: 0.85rem 1.8rem 0.85rem 1.2rem;
  cursor: pointer;
  transition: background 0.14s, color 0.14s;
  outline: none;
  margin-bottom: 0.2rem;
  width: 100%;
  text-align: left;
  box-shadow: ${({ active }) => (active ? '0 2px 8px 0 rgba(0,0,0,0.04)' : 'none')};

  &:hover {
    background: ${({ active }) => (active ? 'rgba(66,153,225,0.13)' : 'rgba(0,0,0,0.03)')};
    color: #fff;
  }
`;

const SidebarStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-width: 110px;
  max-width: 130px;
  background: transparent;
  padding: 1.1rem 0 1.1rem 0;
  border-right: 1px solid rgba(0,0,0,0.07);
  position: relative;
`;


const CloseButtonSidebar = styled.button`
  position: absolute;
  top: 1.1rem;
  left: 0.3rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  border-radius: 50%;
  padding: 0.2rem 0.5rem;
  z-index: 2;
  transition: background 0.14s;
  &:hover {
    background: rgba(0,0,0,0.07);
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const SidebarTabsStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 2.5rem;
  width: 100%;
  align-items: stretch;
  padding-left: 0;
`;

const TabContentAreaStyled = styled.div`
  flex: 1 1 0%;
  min-width: 0;
  padding: 0 1.2rem 1.1rem 1.2rem;
  background: transparent;
  display: flex;
  flex-direction: column;
  min-height: 180px;
  max-height: 420px;
`;


const ContentTitle = styled.h2`
  font-size: 1.08rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 1.1rem 0;
  padding: 0 0 0.6rem 0;
  background: none;
  text-align: left;
  border-bottom: 1.5px solid rgba(0,0,0,0.08);
`;


const FormSection = styled.div`
  padding: 0.2rem 0 0.2rem 0;
  border-bottom: 1px solid rgba(0,0,0,0.07);
  margin-bottom: 1.2rem;
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
`;

const ButtonGroupStyled = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0;
`;

const LLM_HOSTS = [
  { value: 'ollama', label: 'SpaarxSenseAIFabric' },
  { value: 'groq', label: 'Groq' },
  { value: 'google', label: 'Google' },
  { value: 'meta', label: 'Meta' },
  { value: 'microsoft', label: 'Microsoft' },
  { value: 'cloud', label: 'Anthropic' },
  { value: 'openai', label: 'OpenAI' },
];

const LLM_MODELS = [
  { value: 'deepseek-r1:1.5b', label: 'Deepseek R1 1.5B' },
  { value: 'mistral-7b', label: 'Mistral 7B' },
  { value: 'qwen-qwq-32b', label: 'Qwen QWQ 32B' },
  { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash' },
  { value: 'llama-3-8b-instruct', label: 'Llama 3 8B Instruct' },
];

const DEFAULT_PROJECTS = [
  { value: 'gen-ai', label: 'Gen AI' },
  { value: 'project1', label: 'Project 1' },
  { value: 'airways', label: 'Airways' },
];

const GEN_AI_MODES = [
  { value: 'rag', label: 'RAG' },
  { value: 'agentic', label: 'Agentic' },
  { value: 'web-search', label: 'Web' },
];

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  theme: ThemeType;
  initialValues?: {
    genAiMode: string;
    projects: string[];
    llmHost: string;
    llmModel: string;
    llmApiKey: string;
  };
  onSave: (values: {
    genAiMode: string;
    projects: string[];
    llmHost: string;
    llmModel: string;
    llmApiKey: string;
  }) => void;
  loading?: boolean;
  error?: string | null;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  theme: themeMode,
  initialValues,
  onSave,
  loading = false,
  error = null,
}) => {
  // Show loading state if no initialValues are provided
  // Use the theme directly from props for ThemeProvider
  const theme = themeMode === 'dark' ? darkTheme : lightTheme;

  // Always call hooks at the top
  const [formValues, setFormValues] = useState(() => ({
    genAiMode: initialValues?.genAiMode || '',
    projects: Array.isArray(initialValues?.projects) ? [...(initialValues?.projects ?? [])] : [],
    llmHost: initialValues?.llmHost || '',
    llmModel: initialValues?.llmModel || '',
    llmApiKey: initialValues?.llmApiKey || '',
  }));
  useEffect(() => {
    if (initialValues) {
      setFormValues({
        genAiMode: initialValues.genAiMode || '',
        projects: Array.isArray(initialValues.projects) ? [...(initialValues.projects ?? [])] : [],
        llmHost: initialValues.llmHost || '',
        llmModel: initialValues.llmModel || '',
        llmApiKey: initialValues.llmApiKey || '',
      });
    }
  }, [initialValues]);
  const [isProjectsOpen, setIsProjectsOpen] = useState(false);
  const [customProject, setCustomProject] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<'project' | 'llm'>('project');

  // Show loading state if no initialValues are provided
  const loadingSettings = !initialValues;

  // Always run the body scroll lock effect
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (loadingSettings) {
    return isOpen ? (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
        <span>Loading user settings...</span>
      </div>
    ) : null;
  }

  // Derived state
  const isWebSearchMode = formValues.genAiMode === 'web-search';

  // Reset handler
  const handleReset = () => {
    setFormValues({
      genAiMode: initialValues.genAiMode,
      projects: Array.isArray(initialValues.projects) ? [...initialValues.projects] : [],
      llmHost: initialValues.llmHost,
      llmModel: initialValues.llmModel,
      llmApiKey: initialValues.llmApiKey,
    });
    setErrors({});
  };

  // Input change handler
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formValues.llmHost) {
      newErrors.llmHost = 'LLM Host is required';
    }
    
    if (!formValues.llmModel) {
      newErrors.llmModel = 'LLM Model is required';
    }
    
    if ((formValues.llmHost === 'openai' || formValues.llmHost === 'groq') && !formValues.llmApiKey) {
      newErrors.llmApiKey = 'API Key is required for this host';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit handler
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formValues);
      onClose();
    }
  };

  // Add custom project
  const handleAddCustomProject = () => {
    const trimmed = customProject.trim();
    if (!trimmed) return;
    // Prevent duplicates (case-insensitive)
    if (
      formValues.projects.map(p => p.toLowerCase()).includes(trimmed.toLowerCase()) ||
      DEFAULT_PROJECTS.map(p => p.value.toLowerCase()).includes(trimmed.toLowerCase())
    ) {
      setCustomProject('');
      return;
    }
    setFormValues(prev => ({
      ...prev,
      projects: [...prev.projects, trimmed]
    }));
    setCustomProject('');
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay onClick={onClose}>
        <ModalContent 
          style={{
            width: '600px',
            padding: '2rem',
          }}
          onClick={e => e.stopPropagation()}
        >
          <CloseButton 
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
            }}
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </CloseButton>
          <ModalBody>
            {loading ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <span style={{ fontSize: '1.2rem', color: theme.colors.primary }}>Loading settings...</span>
              </div>
            ) : error ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: theme.colors.error }}>
                <span>{error}</span>
              </div>
            ) : (
              <>
                <SidebarStyled>
                  <SidebarTabsStyled>
                    <TabButtonVertical
                      type="button"
                      active={activeTab === 'project'}
                      onClick={() => setActiveTab('project')}
                    >
                      Project
                    </TabButtonVertical>
                    <TabButtonVertical
                      type="button"
                      active={activeTab === 'llm'}
                      onClick={() => setActiveTab('llm')}
                    >
                      LLM
                    </TabButtonVertical>
                  </SidebarTabsStyled>
                </SidebarStyled>
                <TabContentAreaStyled>
                  <ContentTitle>Settings</ContentTitle>
                  <form id="settings-form" onSubmit={handleSubmit}>
                    {activeTab === 'project' && (
                      <>
                        <FormSection>
                          <FormGroup>
                            <Label htmlFor="genAiMode">Gen AI Mode</Label>
                            <ReactSelect
                              name="genAiMode"
                              options={GEN_AI_MODES}
                              value={GEN_AI_MODES.find(opt => opt.value === formValues.genAiMode) || null}
                              onChange={selected => {
                                setFormValues(prev => ({ ...prev, genAiMode: selected ? selected.value : '' }));
                              }}
                              isDisabled={false}
                              placeholder="Select Gen AI Mode..."
                              classNamePrefix="react-select"
                              styles={{
                                control: (base: any) => ({ ...base, borderRadius: '1.2rem', minHeight: 44 }),
                                option: (base: any, state: any) => ({
                                  ...base,
                                  backgroundColor: state.isSelected
                                    ? 'rgba(66,153,225,0.11)'
                                    : state.isFocused
                                    ? 'rgba(66,153,225,0.13)'
                                    : '#fff',
                                  color: '#2d3748',
                                }),
                                singleValue: (base: any) => ({ ...base, color: '#2d3748', fontWeight: 500 }),
                                menu: (base: any) => ({ ...base, borderRadius: '1.1rem', overflow: 'hidden' })
                              }}
                            />
                          </FormGroup>
                        </FormSection>
                        <FormSection>
                          <FormGroup>
                            <Label htmlFor="projects">Project</Label>
                            <ReactSelect
                              isMulti
                              name="projects"
                              options={[
                                ...DEFAULT_PROJECTS,
                                // Add custom projects (not in default list)
                                ...formValues.projects
                                  .filter(
                                    p => !DEFAULT_PROJECTS.some(opt => opt.value === p)
                                  )
                                  .map(p => ({ value: p, label: p }))
                              ]}
                              value={[
                                ...formValues.projects
                                  .map(p => ({ value: p, label: DEFAULT_PROJECTS.find(opt => opt.value === p)?.label || p }))
                              ]}
                              onChange={(selected: MultiValue<{ value: string; label: string }>) => {
                                const values = Array.isArray(selected) ? selected.map(opt => opt.value) : [];
                                setFormValues(prev => ({ ...prev, projects: values }));
                              }}
                              isDisabled={isWebSearchMode}
                              placeholder="Select or add projects..."
                              classNamePrefix="react-select"
                              styles={{
                                control: (base: any) => ({ ...base, borderRadius: '1.2rem', minHeight: 44 }),
                                multiValue: (base: any) => ({ ...base, background: '#e3f0fa', borderRadius: '1rem', padding: '0 4px' }),
                                multiValueLabel: (base: any) => ({ ...base, color: '#2d3748', fontWeight: 500 }),
                                multiValueRemove: (base: any) => ({ ...base, color: '#3182ce', ':hover': { backgroundColor: '#c3e0fa', color: '#1a365d' } }),
                              }}
                            />
                            {/* Custom project entry */}
                            <div style={{ display: 'flex', marginTop: '0.5rem', gap: '0.5rem' }}>
                              <Input
                                type="text"
                                placeholder="Add custom project..."
                                value={customProject}
                                onChange={e => setCustomProject(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleAddCustomProject();
                                  }
                                }}
                                disabled={isWebSearchMode}
                                style={{ flex: 1 }}
                              />
                              <Button
                                type="button"
                                variant="primary"
                                onClick={handleAddCustomProject}
                                disabled={isWebSearchMode || !customProject.trim()}
                                style={{ minWidth: 70 }}
                              >
                                Add
                              </Button>
                            </div>
                            {isWebSearchMode && (
                              <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: theme.colors.disabled }}>
                                Project selection is disabled in Web mode
                              </p>
                            )}
                          </FormGroup>
                        </FormSection>
                      </>
                    )}
                    {activeTab === 'llm' && (
                      <>
                        <FormSection>
                          <FormGroup>
                            <Label htmlFor="llmHost">LLM Host</Label>
                            <ReactSelect
                              name="llmHost"
                              options={LLM_HOSTS}
                              value={LLM_HOSTS.find(opt => opt.value === formValues.llmHost) || null}
                              onChange={selected => {
                                setFormValues(prev => ({ ...prev, llmHost: selected ? selected.value : '' }));
                              }}
                              isDisabled={false}
                              placeholder="Select LLM Host..."
                              classNamePrefix="react-select"
                              styles={{
                                control: (base: any) => ({ ...base, borderRadius: '1.2rem', minHeight: 44 }),
                                option: (base: any, state: any) => ({
                                  ...base,
                                  backgroundColor: state.isSelected
                                    ? 'rgba(66,153,225,0.11)'
                                    : state.isFocused
                                    ? 'rgba(66,153,225,0.13)'
                                    : '#fff',
                                  color: '#2d3748',
                                }),
                                singleValue: (base: any) => ({ ...base, color: '#2d3748', fontWeight: 500 }),
                                menu: (base: any) => ({ ...base, borderRadius: '1.1rem', overflow: 'hidden' })
                              }}
                            />
                          </FormGroup>
                        </FormSection>
                        <FormSection>
                          <FormGroup>
                            <Label htmlFor="llmModel">LLM Model</Label>
                            <Input
                              id="llmModel"
                              name="llmModel"
                              value={formValues.llmModel}
                              onChange={handleInputChange}
                              placeholder="Enter LLM model name (e.g. llama-3-8b-instruct)"
                            />
                            {errors.llmModel && (
                              <ErrorMessage>{errors.llmModel}</ErrorMessage>
                            )}
                          </FormGroup>
                        </FormSection>
                        <FormSection>
                          <FormGroup>
                            <Label htmlFor="llmApiKey">
                              LLM Host API Key
                              {(formValues.llmHost === 'openai' || formValues.llmHost === 'groq') && (
                                <span style={{ color: '#e53e3e', marginLeft: '0.25rem' }}>*</span>
                              )}
                            </Label>
                            <Input
                              type="password"
                              id="llmApiKey"
                              name="llmApiKey"
                              value={formValues.llmApiKey}
                              onChange={handleInputChange}
                              placeholder={`Enter your ${formValues.llmHost} API key`}
                            />
                            {errors.llmApiKey && (
                              <ErrorMessage>{errors.llmApiKey}</ErrorMessage>
                            )}
                             <p style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: theme.colors.disabled }}>
                              {formValues.llmHost === 'ollama' 
                                ? 'No API key needed for local Ollama instances' 
                                : `Your ${formValues.llmHost} API key is stored locally in your browser`}
                            </p>
                          </FormGroup>
                        </FormSection>

                      </>
                    )}
                  </form>
                  <ButtonGroupStyled>
                    <SaveButton
  type="submit"
  form="settings-form"
  disabled={
    !formValues.genAiMode &&
    (!formValues.projects || formValues.projects.length === 0) &&
    !formValues.llmHost &&
    !formValues.llmModel &&
    !formValues.llmApiKey
  }
>
  Save
</SaveButton>
                  </ButtonGroupStyled>
                </TabContentAreaStyled>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </>
  );
};

export default SettingsModal;
